import React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

function MobileSample({ headers, rows }) {
  return (
    <Stack spacing={1} sx={{ display: { xs: "flex", sm: "none" } }}>
      {rows.map((row, rowIndex) => (
        <Paper key={rowIndex} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={0.75}>
            {headers.map((header, columnIndex) => (
              <Box key={`${rowIndex}-${header}`} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>{header}</Typography>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }} title={String(row?.[columnIndex] ?? "")}>
                  {String(row?.[columnIndex] ?? "") || "—"}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

export default function ImportSampleStep({
  draft,
  busy,
  onHeaderRowChange,
  historyAcknowledged,
  onHistoryAcknowledgedChange,
}) {
  const headers = draft?.headers || [];
  const rows = draft?.sampleRows || [];
  const candidates = Array.isArray(draft?.headerCandidates) ? draft.headerCandidates : [];
  const selectedHeaderRowIndex = Number(draft?.headerRowIndex || 0);
  const history = draft?.historyDiagnostic || {};

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Entendamos cómo está armada tu planilla</Typography>
        <Typography variant="body2" color="text.secondary">
          Detectamos {headers.length} columna(s) y {draft?.rowCount || 0} fila(s) candidatas. Si arriba hay un título, datos del proveedor u otra información, podés indicar dónde empiezan realmente los encabezados.
        </Typography>
      </Box>

      <Alert severity="info">
        Fuente: {draft?.source?.fileName || draft?.source?.url || "importación"}
        {draft?.source?.format ? <> · <Chip size="small" label={draft.source.format.toUpperCase()} sx={{ ml: 1 }} /></> : null}
      </Alert>

      {candidates.length ? (
        <Box>
          <Typography sx={{ fontWeight: 900, mb: 0.5 }}>¿Qué fila contiene los nombres de las columnas?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            WebProfit propone una fila, pero podés corregirla sin tocar el archivo original.
          </Typography>
          <Stack spacing={0.75}>
            {candidates.map((candidate) => {
              const selected = candidate.rowIndex === selectedHeaderRowIndex;
              const visibleValues = (candidate.values || []).filter((value) => String(value || "").trim()).slice(0, 5);
              return (
                <Paper
                  key={candidate.rowIndex}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? "action.selected" : "background.paper",
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, px: 1.25, py: 0.75, width: "100%", alignItems: "flex-start" }}
                    control={(
                      <Radio
                        checked={selected}
                        disabled={busy}
                        onChange={() => !selected && onHeaderRowChange?.(candidate.rowIndex)}
                        inputProps={{ "aria-label": `Usar fila ${candidate.rowNumber} como encabezado` }}
                      />
                    )}
                    label={(
                      <Box sx={{ pt: 0.25, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 900 }}>Fila {candidate.rowNumber}</Typography>
                          {selected ? <Chip size="small" color="primary" label="Seleccionada" /> : null}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                          {visibleValues.length ? visibleValues.join(" · ") : "Fila sin valores visibles"}
                        </Typography>
                      </Box>
                    )}
                  />
                </Paper>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      {history?.suspected ? (
        <Alert severity="warning">
          <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Esta planilla puede ser un historial o una lista de movimientos</Typography>
          <Typography variant="body2">{history.message}</Typography>
          {Array.isArray(history.signals) && history.signals.length ? (
            <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
              Señales detectadas: {history.signals.join("; ")}.
            </Typography>
          ) : null}
          <FormControlLabel
            sx={{ mt: 1, alignItems: "flex-start" }}
            control={(
              <Checkbox
                checked={historyAcknowledged === true}
                onChange={(event) => onHistoryAcknowledgedChange?.(event.target.checked)}
              />
            )}
            label="Usar igualmente como catálogo después de revisarlo"
          />
        </Alert>
      ) : null}

      <Box>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>Así queda interpretada la tabla</Typography>
        <MobileSample headers={headers} rows={rows} />
        <TableContainer
          sx={{
            display: { xs: "none", sm: "block" },
            maxHeight: 360,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            maxWidth: "100%",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header} sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {headers.map((header, columnIndex) => (
                    <TableCell key={`${rowIndex}-${header}`} sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" noWrap title={String(row?.[columnIndex] ?? "")}>
                        {String(row?.[columnIndex] ?? "") || "—"}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}
